import ClassNames from "classnames";
import { FunctionComponent, useRef, useState } from "react";
import { RemoveScroll } from "react-remove-scroll";
import { CSSTransition } from "react-transition-group";
import headerBaseStyles from "../../stylesheets/components/header-base.module.css";
import styles from "../../stylesheets/components/header-mobile/header-3-mobile.module.css";
import imageOverlayStyles from "../../stylesheets/components/image-overlay.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import { CommonHeaderLayoutProps, Language } from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import {
  useIntersection,
  useKeyDown,
  useScrollDirection,
} from "../../utils/hooks.js";
import {
  getEmailLink,
  getPhoneLink,
  getQuickLinksWithIcon,
  getWhatsAppLink,
  onTransitionEnd,
  overrideImagesAspectRatio,
} from "../../utils/utils.js";
import Icon from "../Icon.js";
import MainMenuListCollapsible, {
  verticalMenuClassNames,
} from "../MainMenuListCollapsible.js";
import ImagesModule from "../Modules/ImagesModule.js";
import SmartLink from "../SmartLink.js";
import BookEnquireLinks from "./BookEnquireLinks.js";
import CircleHamburgerButton from "./CircleHamburgerButton.js";
import MobileLanguageMenu from "./MobileLanguageMenu.js";
import MobileMainLogo from "./MobileMainLogo.js";
import QuickButtons from "./QuickButtons.js";

const Header3Mobile: FunctionComponent<CommonHeaderLayoutProps> = ({
  accommodation,
  actionLinks,
  activeModuleId,
  activePagePath,
  fallbackLanguageId,
  imagesModule: imagesModuleProp,
  isHomePage,
  isPreview,
  languageId,
  logo,
  logoSize,
  mainPageURL,
  menuItems,
  pageId,
  pages,
  queries,
  scheme,
  sticky,
  whatsAppNumber,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const scrollDirection = useScrollDirection();
  const ref = useRef<HTMLDivElement>(null);
  const barNodeRef = useRef<HTMLDivElement>(null);
  const menuNodeRef = useRef<HTMLDivElement>(null);
  const intersection = useIntersection(ref);
  const isIntersecting = intersection?.isIntersecting ?? true;
  const imagesModule = imagesModuleProp
    ? overrideImagesAspectRatio(imagesModuleProp)
    : undefined;

  useKeyDown("Escape", () => setIsMenuOpen(false));

  const isFixed =
    !isPreview &&
    ((scrollDirection === "up" && !isIntersecting) || sticky || isMenuOpen);

  const showQuickButtons =
    isPreview ||
    isIntersecting ||
    sticky ||
    isMenuOpen ||
    scrollDirection === "up";

  const quickNav = [
    getEmailLink(accommodation, languageId),
    ...(whatsAppNumber ? [getWhatsAppLink(whatsAppNumber)] : []),
    ...getQuickLinksWithIcon(actionLinks),
  ];

  const phoneLink = getPhoneLink(accommodation, languageId);

  return (
    <div className={headerBaseStyles["HeaderModule__Inner--narrow"]}>
      <header
        className={ClassNames(
          styles.Header3Mobile,
          styles[`Header3Mobile--height-${logoSize}`],
          {
            [styles["Header3Mobile--is-home"]]: isHomePage,
            [styles["Header3Mobile--preview"]]: isPreview,
            [styles["Header3Mobile--scrolled-down"]]: !isIntersecting,
            [styles["Header3Mobile--no-image"]]: !imagesModule?.pageId,
          },
          imageOverlayStyles.ImageOverlay__Header3Mobile,
        )}
      >
        <div
          ref={ref}
          className={ClassNames(styles.Header3Mobile__IntersectionHelper, {
            [styles["Header3Mobile__IntersectionHelper--scrolled-down"]]:
              !isIntersecting,
          })}
        />

        <CSSTransition
          timeout={300}
          in={isFixed}
          addEndListener={(done) => {
            barNodeRef.current && onTransitionEnd(barNodeRef.current, done);
          }}
          nodeRef={barNodeRef}
          classNames={{
            enter: styles["Header3Mobile__Bar--enter"],
            enterActive: styles["Header3Mobile__Bar--enter-active"],
            exit: ClassNames(
              styles["Header3Mobile__Bar--fixed"],
              styles["Header3Mobile__Bar--exit"],
            ),
            exitActive: ClassNames(
              styles["Header3Mobile__Bar--fixed"],
              styles["Header3Mobile__Bar--exit-active"],
            ),
          }}
        >
          <div
            ref={barNodeRef}
            className={ClassNames(
              styles.Header3Mobile__Bar,
              {
                [styles["Header3Mobile__Bar--fixed"]]: isFixed,
                [styles["Header3Mobile__Bar--scrolled-down"]]: !isIntersecting,
                [styles["Header3Mobile__Bar--menu-open"]]: isMenuOpen,
              },
              css({
                background: scheme.main.background,
                color: scheme.main.text,
              }),
            )}
          >
            <div
              className={ClassNames(
                styles.Header3Mobile__BarInner,
                moduleBaseStyles.Module__Wrapper,
              )}
            >
              <Header3LogoContainer
                isMenuOpen={isMenuOpen}
                toggleMenuOpen={() => setIsMenuOpen((v) => !v)}
                languageId={languageId}
              />
              <div className={styles.Header3Mobile__LogoContainer}>
                <MobileMainLogo
                  accommodationName={accommodation.name}
                  isPreview={isPreview}
                  logo={logo}
                  mainPageURL={mainPageURL}
                  isSmall={!isIntersecting}
                  size={logoSize}
                />
              </div>
              <div className={styles.Header3Mobile__Container3}>
                <BookEnquireLinks
                  actionLinks={actionLinks}
                  isPreview={isPreview}
                  onClick={() => setIsMenuOpen(false)}
                />
                <div>
                  <a
                    href={phoneLink.href}
                    className={ClassNames(
                      "BareBtn",
                      styles.Header3Mobile__Phone,
                    )}
                    title={phoneLink.title}
                  >
                    <Icon glyph={phoneLink.icon} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </CSSTransition>

        <CSSTransition
          in={isMenuOpen}
          timeout={2000}
          mountOnEnter={true}
          unmountOnExit={true}
          addEndListener={(done) => {
            menuNodeRef.current && onTransitionEnd(menuNodeRef.current, done);
          }}
          nodeRef={menuNodeRef}
          classNames={{
            enterActive: styles["Header3Mobile__Menu--open"],
            enterDone: styles["Header3Mobile__Menu--open"],
          }}
        >
          <div
            ref={menuNodeRef}
            className={ClassNames(
              styles.Header3Mobile__Menu,
              css({
                background: scheme.main.background,
                color: scheme.main.text,
              }),
            )}
          >
            <RemoveScroll
              enabled={!isPreview}
              className={styles.Header3Mobile__MenuInner}
            >
              <MobileLanguageMenu
                className={styles.Header3Mobile__LanguageMenu}
                isPreview={isPreview}
                languageId={languageId}
                pageId={pageId}
                onClick={() => setIsMenuOpen(false)}
              />
              <MainMenuListCollapsible
                isPreview={isPreview}
                languageId={languageId}
                onMenuItemClick={() => setIsMenuOpen(false)}
                menuItems={menuItems}
                pages={pages}
                activePagePath={activePagePath}
                fallbackLanguageId={fallbackLanguageId}
                menuLevel={0}
                collapseLevel={0}
                classNames={verticalMenuClassNames}
              />
              <ul className={styles.Header3Mobile__QuickNav}>
                {quickNav.map(({ href, icon, type }) => (
                  <li key={type}>
                    <SmartLink
                      isPreview={isPreview}
                      anchorAttrs={{
                        href,
                        className: styles.Header3Mobile__QuickNavLink,
                        onClick: () => type !== "email" && setIsMenuOpen(false),
                      }}
                    >
                      <Icon
                        glyph={icon}
                        className={styles.Header3Mobile__QuickNavIcon}
                      />
                    </SmartLink>
                  </li>
                ))}
              </ul>
            </RemoveScroll>
          </div>
        </CSSTransition>

        {imagesModule?.pageId && (
          <ImagesModule
            translatedModule={imagesModule}
            isPreview={isPreview}
            pageId={pageId}
            queries={queries}
            isActive={false}
            isInsideHeader={true}
            isFirstOnPage={false}
            parentScheme={scheme}
            activeModuleId={activeModuleId}
            fullHeight={true}
            className={styles.ImagesModule}
          />
        )}
      </header>

      <QuickButtons
        actionLinks={actionLinks}
        className={css({
          background: scheme.primary.background,
          color: scheme.primary.text,
        })}
        isPreview={isPreview}
        isVisible={showQuickButtons}
        onClick={() => setIsMenuOpen(false)}
      />
    </div>
  );
};

interface LogoProps {
  isMenuOpen: boolean;
  languageId: Language;
  toggleMenuOpen: () => void;
}

export const Header3LogoContainer: FunctionComponent<LogoProps> = ({
  isMenuOpen,
  languageId,
  toggleMenuOpen,
}) => (
  <div>
    <CircleHamburgerButton
      isMenuOpen={isMenuOpen}
      languageId={languageId}
      onClick={toggleMenuOpen}
    />
  </div>
);

export default Header3Mobile;
